import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import StatusBadge from "components/common/StatusBadge";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { getMaterialRequests } from "features/MaterialRequests/services/MaterialRequestService";
import type { MaterialRequest } from "features/MaterialRequests/types/material-request.types";
import {
  MaterialRequestSourceType,
  MaterialRequestStatusMap,
} from "features/MaterialRequests/constants/material-request.constants";
import { getMaterialRequestFromJobPath } from "features/MaterialRequests/utils/materialRequestFromJobPaths";
import { materialRequestDisplayCode } from "features/MaterialRequests/utils/material-request.utils";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useWorkOrderOverview } from "./WorkOrderOverviewContext";

export default function MaterialRequestsTab() {
  const { workOrder } = useWorkOrderOverview();
  const { can } = useAccess();
  const canRead = can(Permissions.MATERIAL_REQUEST.READ);
  const canCreate = can(Permissions.MATERIAL_REQUEST.CREATE);
  const tableRef = useRef<() => void>(null);

  const sourceId = workOrder?.id;

  const requestParams = useMemo(
    () => ({
      sourceType: MaterialRequestSourceType.WORK_ORDER,
      sourceId: sourceId ?? 0,
    }),
    [sourceId],
  );

  const columns: ColumnDef<MaterialRequest>[] = useMemo(
    () => [
      IndexColumn<MaterialRequest>(),
      {
        header: "Code",
        id: "code",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const code = materialRequestDisplayCode(row.original);
          return canRead ? (
            <Link
              to={`/inventory-management/material-requests/${row.original.id}`}
              className="fw-medium link"
            >
              {code}
            </Link>
          ) : (
            <span className="fw-medium">{code}</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={MaterialRequestStatusMap}
          />
        ),
      },
      {
        header: "Requested on",
        id: "createdAt",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [canRead],
  );

  if (sourceId == null) return null;

  return (
    <Card>
      <CardHeader>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h6 className="mb-0 text-uppercase">Material Requests</h6>
          {canCreate ? (
            <Link
              to={getMaterialRequestFromJobPath(
                MaterialRequestSourceType.WORK_ORDER,
                sourceId,
              )}
              className="btn btn-primary btn-sm btn-label"
            >
              <i className="ri-add-line label-icon align-middle fs-16 me-1" />
              Request materials
            </Link>
          ) : null}
        </div>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getMaterialRequests}
          requestParams={requestParams}
          refreshRef={tableRef}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-nowrap mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
